import { type Redirect, type Rewrite } from 'next/dist/lib/load-custom-routes';

/**
 * Duplicated from nexus-core which cannot be imported here,
 * due to Next.js not supporting it here (next.config.ts).
 * @extends {import('@nfctron/nexus-core').REQ_SOURCE}
 */
enum REQ_SOURCE {
	WEB = 1,
	WEB_UNITED_TICKETS = 4,
	WEB_DINO_LIVE = 5,
}
/**
 * Configuration interface for DNS host settings
 */
export interface DnsHostConfig {
	reqSource: REQ_SOURCE.WEB | REQ_SOURCE.WEB_UNITED_TICKETS | REQ_SOURCE.WEB_DINO_LIVE;
	scopes: {
		organizers: Array<string | OrganizerScopeMatcher>;
		eventGroups: Array<string | EventGroupScopeMatcher>;
	};
	urls: {
		getHomeUrl: (input: { locale: string | undefined }) => URL;
		getTermsUrl?: (input: { eventGroupSlug: string | null; locale: string | undefined }) => URL;
		getEventUrl?: (input: { organizerSlug: string; eventSlug: string; locale: string | undefined }) => URL;
		getEventGroupUrl?: (input: { eventGroupSlug: string; locale: string | undefined }) => URL;
		getOrganizerUrl?: (input: { organizerSlug: string; locale: string | undefined }) => URL;
	};
	rules: {
		robots?: Array<string>;
		rewrite?: Array<Rewrite>;
		redirect?: Array<Redirect>;
	};
}

/**
 * Resolved configuration interface with all required fields
 * @extends {DnsHostConfig}
 * @template THost - The type of the hostname
 */
export interface DnsHostConfigResolved<THost extends DnsHost> extends Omit<DnsHostConfig, 'urls'> {
	hostname: THost;
	urls: {
		getHomeUrl: NonNullable<DnsHostConfig['urls']['getHomeUrl']>;
		getTermsUrl: DnsHostConfig['urls']['getTermsUrl'] | null;
		getEventUrl: NonNullable<DnsHostConfig['urls']['getEventUrl']>;
		getEventGroupUrl: NonNullable<DnsHostConfig['urls']['getEventGroupUrl']>;
		getOrganizerUrl: NonNullable<DnsHostConfig['urls']['getOrganizerUrl']>;
	};
	rules: Required<DnsHostConfig['rules']>;
}

type OrganizerScopeMatcher = (input: { organizerSlug: string; eventSlug?: string }) => boolean;
type EventGroupScopeMatcher = (input: { eventGroupSlug: string }) => boolean;

/**
 * Creates rules configuration with defaults
 * @param rules - Base rules configuration
 * @param configForDefaults - Optional default configuration
 * @returns Complete rules configuration
 */
function createRules(
	rules: DnsHostConfig['rules'],
	configForDefaults?: {
		indexRewriteTo: string | null;
		eventRewrite: { destination: string; source: 'koncert' | 'festival' } | null;
		termsRewrite: string | null;
	},
): DnsHostConfig['rules'] {
	return {
		...rules,
		rewrite: [
			configForDefaults?.indexRewriteTo
				? {
						source: '/',
						destination: configForDefaults.indexRewriteTo,
					}
				: null,
			configForDefaults?.eventRewrite
				? {
						source: `/${configForDefaults.eventRewrite.source}/:slug(.*)`,
						destination: configForDefaults.eventRewrite.destination,
					}
				: null,
			configForDefaults?.termsRewrite
				? {
						source: '/obchodni-podminky',
						destination: configForDefaults.termsRewrite,
					}
				: null,
			...(rules.rewrite ?? []),
		].filter((r) => r !== null),
	};
}

/**
 * List of registered DNS hosts with their configurations
 * @see https://vercel.com/nfctron/nfctron-tickets/settings/domains
 */
export const dnsHosts = {
	/**
	 * United Tickets
	 * @see https://store.united-tickets.cz
	 */
	'store.united-tickets.cz': {
		reqSource: REQ_SOURCE.WEB_UNITED_TICKETS,
		urls: {
			getHomeUrl() {
				return new URL('https://www.united-tickets.cz');
			},
		},
		scopes: {
			organizers: [
				/** universal music (without mirai) */
				({ organizerSlug, eventSlug }) => {
					if (organizerSlug === 'universal-music' && !eventSlug?.includes('mirai-summer-fest-2025')) return true;
					return false;
				},
			],
			eventGroups: ['vesna-muzika-slavica-2025', 'poetika-2025', 'gaia-mesiah-elements-tour', 'dorian-bmw-backseat-tour'],
		},
		rules: createRules(
			{
				robots: [
					/** disallow all organizers but universal-music */
					'Disallow: /organizer/*',
					'Allow: /organizer/universal-music',
					/** disallow all mirai events */
					'Disallow: /event/universal-music/mirai-summer-fest-2025',
				],
				redirect: [
					/** index redirect to main page */
					{
						source: '/',
						destination: 'https://www.united-tickets.cz',
						permanent: true,
					},
					/** Mirai Summer Fest redirect */
					{
						source: '/event/universal-music/mirai-summer-fest-2025(.*)?',
						destination: 'https://vstupenky.miraifest.cz',
						permanent: true,
					},
					{
						source: '/tour/mirai-summer-fest-2025',
						destination: 'https://vstupenky.miraifest.cz',
						permanent: true,
					},
					/** changed POA / Jamaron redirect */
					{
						source: '/event/universal-music/jamaron-plovarna-2025',
						destination: 'https://vstupenky.pragueopenair.cz/event/prague-open-air-sro/jamaron-2025',
						permanent: true,
					},
				],
			},
			{
				indexRewriteTo: null,
				eventRewrite: null,
				termsRewrite: null,
			},
		),
	},
	/**
	 * Universal Music / Miraifest
	 * @see https://vstupenky.miraifest.cz
	 */
	'vstupenky.miraifest.cz': {
		reqSource: REQ_SOURCE.WEB_UNITED_TICKETS,
		urls: {
			getHomeUrl() {
				return new URL('https://miraifest.cz');
			},
			getEventUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/koncert/${input.eventSlug}`, 'https://vstupenky.miraifest.cz');
			},
			getEventGroupUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(localePath, 'https://vstupenky.miraifest.cz');
			},
			getTermsUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/obchodni-podminky`, 'https://vstupenky.miraifest.cz');
			},
		},
		scopes: {
			organizers: [
				/** universal music but only mirai */
				({ organizerSlug, eventSlug }) => {
					if (organizerSlug === 'universal-music' && eventSlug?.includes('mirai-summer-fest-2025')) return true;
					return false;
				},
			],
			eventGroups: ['mirai-summer-fest-2025'],
		},
		rules: createRules(
			{
				robots: [
					/** disallow all organizers */
					'Disallow: /organizer/*',
					/** disallow all events but mirai */
					'Disallow: /event/*',
					'Allow: /event/universal-music/mirai-summer-fest-2025',
					/** disallow all tours but mirai */
					'Disallow: /tour/*',
					'Allow: /tour/mirai-summer-fest-2025',
				],
			},
			{
				indexRewriteTo: '/tour/mirai-summer-fest-2025',
				eventRewrite: {
					source: 'koncert',
					destination: '/event/universal-music/:slug',
				},
				termsRewrite: '/tour/mirai-summer-fest-2025/terms-and-conditions',
			},
		),
	},
	/**
	 * Marek Ztracený
	 * @see https://vstupenky.marekztraceny.cz
	 */
	'vstupenky.marekztraceny.cz': {
		reqSource: REQ_SOURCE.WEB,
		urls: {
			getHomeUrl() {
				return new URL('https://marekztraceny.cz');
			},
			getTermsUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/obchodni-podminky`, 'https://vstupenky.marekztraceny.cz');
			},
			getEventUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/koncert/${input.eventSlug}`, 'https://vstupenky.marekztraceny.cz');
			},
			getEventGroupUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(localePath, 'https://vstupenky.marekztraceny.cz');
			},
		},
		scopes: {
			organizers: ['marek-ztraceny'],
			eventGroups: ['marek-ztraceny-je-zpatky-tour-2025'],
		},
		rules: createRules(
			{
				rewrite: [
					/** rodina rewrite */
					{
						source: '/ztracena-rodina',
						destination: '/tour/marek-ztraceny-je-zpatky-tour-2025?promoCode=rodina',
					},
				],
			},
			{
				indexRewriteTo: '/tour/marek-ztraceny-je-zpatky-tour-2025',
				eventRewrite: {
					source: 'koncert',
					destination: '/event/marek-ztraceny/:slug',
				},
				termsRewrite: '/tour/marek-ztraceny-je-zpatky-tour-2025/terms-and-conditions',
			},
		),
	},
	/**
	 * Lovestream
	 * @see https://tickets.lovestream.sk
	 */
	'tickets.lovestream.sk': {
		reqSource: REQ_SOURCE.WEB,
		urls: {
			getHomeUrl() {
				return new URL('https://www.lovestream.sk');
			},
		},
		scopes: {
			organizers: ['silvi-production'],
			eventGroups: [],
		},
		rules: createRules(
			{},
			{
				indexRewriteTo: '/event/silvi-production/lovestream-festival-2025',
				eventRewrite: {
					source: 'festival',
					destination: '/event/silvi-production/:slug',
				},
				termsRewrite: null,
			},
		),
	},
	/**
	 * Chinaski
	 * @see https://vstupenky.chinaski.cz
	 */
	'vstupenky.chinaski.cz': {
		reqSource: REQ_SOURCE.WEB,
		urls: {
			getHomeUrl() {
				return new URL('https://chinaski.cz');
			},
			getTermsUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/obchodni-podminky`, 'https://vstupenky.chinaski.cz');
			},
			getEventUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/koncert/${input.eventSlug}`, 'https://vstupenky.chinaski.cz');
			},
			getEventGroupUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(localePath, 'https://vstupenky.chinaski.cz');
			},
		},
		scopes: {
			organizers: ['matchfire-sro'],
			eventGroups: ['chinaski-2025'],
		},
		rules: createRules(
			{},
			{
				indexRewriteTo: '/tour/chinaski-2025',
				eventRewrite: {
					source: 'koncert',
					destination: '/event/matchfire-sro/:slug',
				},
				termsRewrite: '/tour/chinaski-2025/terms-and-conditions',
			},
		),
	},
	/**
	 * Hrady.cz
	 * @see https://vstupenky.hradycz.cz
	 */
	'vstupenky.hradycz.cz': {
		reqSource: REQ_SOURCE.WEB,
		urls: {
			getHomeUrl() {
				return new URL('https://hradycz.cz');
			},
			getTermsUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/obchodni-podminky`, 'https://vstupenky.hradycz.cz');
			},
			getEventUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/festival/${input.eventSlug}`, 'https://vstupenky.hradycz.cz');
			},
			getEventGroupUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(localePath, 'https://vstupenky.hradycz.cz');
			},
		},
		scopes: {
			organizers: ['riff-raff-sro'],
			eventGroups: ['hrady-cz-2025'],
		},
		rules: createRules(
			{},
			{
				indexRewriteTo: '/tour/hrady-cz-2025',
				eventRewrite: {
					source: 'festival',
					destination: '/event/riff-raff-sro/:slug',
				},
				termsRewrite: '/tour/hrady-cz-2025/terms-and-conditions',
			},
		),
	},
	/**
	 * MKLV Fest
	 * @see https://vstupenky.mklvfest.cz
	 */
	'vstupenky.mklvfest.cz': {
		reqSource: REQ_SOURCE.WEB,
		urls: {
			getHomeUrl() {
				return new URL('https://mklvfest.cz');
			},
			getEventUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/festival/${input.eventSlug}`, 'https://vstupenky.mklvfest.cz');
			},
		},
		scopes: {
			organizers: ['art-delivery'],
			eventGroups: [],
		},
		rules: createRules(
			{},
			{
				indexRewriteTo: '/event/art-delivery/mklv-fest-2025',
				eventRewrite: {
					source: 'festival',
					destination: '/event/art-delivery/:slug',
				},
				termsRewrite: null,
			},
		),
	},
	/**
	 * Prague Harley Days
	 * @see https://vstupenky.pragueharleydays.cz
	 */
	'vstupenky.pragueharleydays.cz': {
		reqSource: REQ_SOURCE.WEB,
		urls: {
			getHomeUrl() {
				return new URL('https://pragueharleydays.cz');
			},
			getTermsUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/obchodni-podminky`, 'https://vstupenky.pragueharleydays.cz');
			},
		},
		scopes: {
			organizers: ['prague-harley-days-1649858729634'],
			eventGroups: [],
		},
		rules: createRules(
			{},
			{
				indexRewriteTo: '/event/prague-harley-days-1649858729634/prague-harley-days-2025',
				eventRewrite: null,
				termsRewrite: null,
			},
		),
	},
	/**
	 * Prague Open Air
	 * @see https://vstupenky.pragueopenair.cz
	 */
	'vstupenky.pragueopenair.cz': {
		reqSource: REQ_SOURCE.WEB,
		urls: {
			getHomeUrl() {
				return new URL('https://pragueopenair.cz');
			},
			getEventUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/koncert/${input.eventSlug}`, 'https://vstupenky.pragueopenair.cz');
			},
			getEventGroupUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(localePath, 'https://vstupenky.pragueopenair.cz');
			},
			getTermsUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/obchodni-podminky`, 'https://vstupenky.pragueopenair.cz');
			},
		},
		scopes: {
			organizers: ['prague-open-air-sro'],
			eventGroups: ['prague-open-air'],
		},
		rules: createRules(
			{},
			{
				indexRewriteTo: '/tour/prague-open-air',
				eventRewrite: {
					source: 'koncert',
					destination: '/event/prague-open-air-sro/:slug',
				},
				termsRewrite: '/tour/prague-open-air/terms-and-conditions',
			},
		),
	},
	/**
	 * Soldout Festival
	 * @see https://vstupenky.soldoutfestival.cz
	 */
	'vstupenky.soldoutfestival.cz': {
		reqSource: REQ_SOURCE.WEB,
		urls: {
			getHomeUrl() {
				return new URL('https://soldoutfestival.cz');
			},
			getEventUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/festival/${input.eventSlug}`, 'https://vstupenky.soldoutfestival.cz');
			},
			getTermsUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/obchodni-podminky`, 'https://vstupenky.soldoutfestival.cz');
			},
		},
		scopes: {
			organizers: ['event4life-sro'],
			eventGroups: [],
		},
		rules: createRules(
			{},
			{
				indexRewriteTo: '/event/event4life-sro/soldout-festival-2025',
				eventRewrite: {
					source: 'festival',
					destination: '/event/event4life-sro/:slug',
				},
				termsRewrite: null,
			},
		),
	},
	/**
	 * Symphonika
	 * @see https://vstupenky.symphonika.cz
	 */
	'vstupenky.symphonika.cz': {
		reqSource: REQ_SOURCE.WEB,
		urls: {
			getHomeUrl() {
				return new URL('https://www.symphonika.cz/');
			},
			getEventGroupUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(localePath, 'https://vstupenky.symphonika.cz');
			},
			getTermsUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/obchodni-podminky`, 'https://vstupenky.symphonika.cz');
			},
		},
		scopes: {
			organizers: ['cbi-production'],
			eventGroups: ['symphonika-on-the-rock-2025'],
		},
		rules: createRules(
			{},
			{
				indexRewriteTo: '/tour/symphonika-on-the-rock',
				eventRewrite: null,
				termsRewrite: '/tour/symphonika-on-the-rock/terms-and-conditions',
			},
		),
	},
	/**
	 * Prague Car Festival
	 * @see https://vstupenky.praguecarfestival.cz
	 */
	'vstupenky.praguecarfestival.cz': {
		reqSource: REQ_SOURCE.WEB,
		urls: {
			getHomeUrl() {
				return new URL('https://praguecarfestival.cz');
			},
			getEventUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/festival/${input.eventSlug}`, 'https://vstupenky.praguecarfestival.cz');
			},
		},
		scopes: {
			organizers: ['prague-car-festival-sro'],
			eventGroups: [],
		},
		rules: createRules(
			{},
			{
				indexRewriteTo: '/event/prague-car-festival-sro/prague-car-festival-2025',
				eventRewrite: {
					source: 'festival',
					destination: '/event/prague-car-festival-sro/:slug',
				},
				termsRewrite: null,
			},
		),
	},
	/**
	 * Zamosti
	 * @see https://vstupenky.zamosti.cz
	 */
	'vstupenky.zamosti.cz': {
		reqSource: REQ_SOURCE.WEB,
		urls: {
			getHomeUrl() {
				return new URL('https://zamosti.cz');
			},
			getEventUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/festival/${input.eventSlug}`, 'https://vstupenky.zamosti.cz');
			},
		},
		scopes: {
			organizers: ['zamosti'],
			eventGroups: [],
		},
		rules: createRules(
			{},
			{
				indexRewriteTo: '/event/zamosti/zamosti-2025',
				eventRewrite: {
					source: 'festival',
					destination: '/event/zamosti/:slug',
				},
				termsRewrite: null,
			},
		),
	},
	/**
	 * Dinolive
	 * @see https://tickets.dinolive.cz
	 */
	'tickets.dinolive.cz': {
		reqSource: REQ_SOURCE.WEB_DINO_LIVE,
		urls: {
			getHomeUrl() {
				return new URL('https://dinolive.cz');
			},
		},
		scopes: {
			organizers: ['sidetoside'],
			eventGroups: [],
		},
		rules: createRules(
			{},
			{
				indexRewriteTo: '/event/sidetoside/dinolive-2025',
				eventRewrite: {
					source: 'festival',
					destination: '/event/sidetoside/:slug',
				},
				termsRewrite: null,
			},
		),
	},
	/**
	 * Majáles Pardubice
	 * @see https://vstupenky.majalespardubice.cz
	 */
	'vstupenky.majalespardubice.cz': {
		reqSource: REQ_SOURCE.WEB,
		urls: {
			getHomeUrl() {
				return new URL('https://majalespardubice.cz');
			},
			getEventUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/festival/${input.eventSlug}`, 'https://vstupenky.majalespardubice.cz');
			},
		},
		scopes: {
			organizers: ['majales-pardubice'],
			eventGroups: [],
		},
		rules: createRules(
			{},
			{
				indexRewriteTo: '/event/majales-pardubice/majales-pardubice-2025',
				eventRewrite: {
					source: 'festival',
					destination: '/event/majales-pardubice/:slug',
				},
				termsRewrite: null,
			},
		),
	},
} as const satisfies Record<string, DnsHostConfig>;

/**
 * Typed registered DNS hostnames
 * @see {@link dnsHosts}
 */
export type DnsHost = keyof typeof dnsHosts;

/**
 * Gets DNS host configuration for a given hostname
 * @param hostname The hostname to get configuration for
 * @returns The DNS host configuration or null if not found
 */
export function getDnsHostConfig<THost extends DnsHost>(hostname: THost | (string & {})): DnsHostConfigResolved<THost> | null {
	if (!(hostname in dnsHosts)) {
		return null;
	}

	const config = dnsHosts[hostname as DnsHost];
	const baseUrl = `https://${hostname}`;

	return {
		...config,
		hostname: hostname as THost,
		urls: {
			/** termsUrl by default is not used */
			getTermsUrl: null,
			/** default getEventUrl implementation */
			getEventUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/event/${input.organizerSlug}/${input.eventSlug}`, baseUrl);
			},
			/** default getEventGroupUrl implementation */
			getEventGroupUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/tour/${input.eventGroupSlug}`, baseUrl);
			},
			/** default getOrganizerUrl implementation */
			getOrganizerUrl(input) {
				const localePath = input.locale ? `/${input.locale}` : '';
				return new URL(`${localePath}/organizer/${input.organizerSlug}`, baseUrl);
			},
			/** merge with custom implementations */
			...config.urls,
		},
		rules: {
			rewrite: config.rules.rewrite ?? [],
			redirect: config.rules.redirect ?? [],
			robots: config.rules.robots ?? [],
		},
	};
}
